import { useState, useEffect } from 'react';
import { MapPin, Download, ArrowLeft, Building2, FileText, MapPinned, Shield, Zap, ArrowRight, ChevronDown, Lock, Clock, Phone, MessageCircle, ExternalLink } from 'lucide-react';
import { api } from '@/utils/api';
import InvestmentBookingModal from './InvestmentBookingModal';

// Investor Cashback Tiers (Based on Investment Amount)
// Base: ₹0, Silver: ₹10L, Gold: ₹30L, Platinum: ₹90L, Diamond: ₹2.7Cr
const investorTiers = [
  { name: 'Base', threshold: 0, cashback: 0.5, icon: '🌱' },
  { name: 'Silver', threshold: 1000000, cashback: 1.0, icon: '🥈' },      // ₹10 Lakh
  { name: 'Gold', threshold: 3000000, cashback: 1.5, icon: '🥇' },        // ₹30 Lakh
  { name: 'Platinum', threshold: 9000000, cashback: 2.0, icon: '💎' },    // ₹90 Lakh
  { name: 'Diamond', threshold: 27000000, cashback: 3.0, icon: '👑' },    // ₹2.7 Crore
];

const UNIT_STATE_CONFIG = {
  DRAFT: { label: 'Coming Soon', color: 'text-gray-400 bg-gray-500/20 border-gray-500/30', canInvest: false },
  OPEN: { label: 'Open for Investment', color: 'text-[#4ADE80] bg-[#4ADE80]/20 border-[#4ADE80]/30', canInvest: true },
  PAUSED: { label: 'Temporarily Paused', color: 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30', canInvest: false },
  FUNDED: { label: 'Fully Funded', color: 'text-purple-400 bg-purple-500/20 border-purple-500/30', canInvest: false },
  CLOSED: { label: 'Closed', color: 'text-blue-400 bg-blue-500/20 border-blue-500/30', canInvest: false },
  EXITED: { label: 'Exited', color: 'text-white bg-white/20 border-white/30', canInvest: false },
};

export default function PropertyDetailContent({ property: propData, onBack, onBookInvestment, unitLifecycleFilter = 'open' }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [investmentAmount, setInvestmentAmount] = useState(500000);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Raw project data from API (has nested rera, developer, location etc.)
  const rawProject = propData?._raw || {};

  // Fetch units for this project based on lifecycle filter
  useEffect(() => {
    const fetchUnits = async () => {
      if (!propData?.id) return;
      
      setLoadingUnits(true);
      setSelectedUnit(null);
      
      try {
        // Include lifecycle filter in API call
        const filterParam = unitLifecycleFilter ? `?lifecycleFilter=${unitLifecycleFilter}` : '';
        const response = await api.get(`/projects/${propData.id}/units${filterParam}`);
        const fetchedUnits = response.data?.units || [];
        setUnits(fetchedUnits);
        
        // Auto-select first unit if available
        if (fetchedUnits.length > 0) {
          setSelectedUnit(fetchedUnits[0]);
        }
      } catch (error) {
        console.error('Error fetching units:', error);
        setUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    };

    fetchUnits();
  }, [propData?.id, unitLifecycleFilter]);

  // Get unit-specific financial data or fall back to project data
  const getFinancialData = () => {
    if (selectedUnit) {
      return {
        unitValue: selectedUnit.financial?.unitValue ?? 0,
        capitalTarget: selectedUnit.financial?.capitalTarget ?? 0,
        capitalRaised: selectedUnit.financial?.capitalRaised ?? 0,
        minInvestment: selectedUnit.financial?.minInvestment ?? 500000,
        maxInvestment: selectedUnit.financial?.maxInvestment ?? 10000000,
        unitSize: selectedUnit.specifications?.size || '',
        configuration: selectedUnit.specifications?.configuration || '',
        llpName: selectedUnit.llp?.name || ''
      };
    }
    return {
      unitValue: propData?.value ?? 0,
      capitalTarget: propData?.capitalTarget ?? 0,
      capitalRaised: propData?.capitalRaised ?? 0,
      minInvestment: propData?.minInv ?? 500000,
      maxInvestment: propData?.maxInv ?? 10000000,
      unitSize: propData?.unitSize || '',
      configuration: propData?.configuration || '',
      llpName: ''
    };
  };

  const financials = getFinancialData();

  // Project level data
  const property = {
    id: propData?.id,
    name: propData?.name || 'Property',
    location: propData?.location || '',
    sector: propData?.sector || '',
    developer: propData?.developer || 'Developer',
    eoiFee: 25000,
    completionDate: 'Q4 2027',
    reraNumber: propData?.rera || 'HRERA-XXX',
    image: propData?.image || 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200',
    images: propData?.images || [],
    advisor: propData?.advisor || null,
    expectedROI: propData?.expectedROI || '35-40%',
    expectedTenure: propData?.expectedTenure || '2-3 years',
    badges: propData?.badges || [],
    historicalReturns: [{ year: 2023, return: 11.2 }, { year: 2024, return: 12.8 }, { year: 2025, return: 14.3 }],
    areaAverageReturn: 13.1,
    milestones: [
      { name: 'Foundation', date: '2024-Q1', status: 'completed', percent: 100 },
      { name: 'Structure', date: '2025-Q2', status: 'completed', percent: 100 },
      { name: 'Finishing', date: '2026-Q3', status: 'in-progress', percent: 65 },
      { name: 'Handover', date: '2027-Q4', status: 'pending', percent: 0 }
    ]
  };

  const balanceToFund = financials.capitalTarget - financials.capitalRaised;
  const fundingPercentage = financials.capitalTarget > 0 ? (financials.capitalRaised / financials.capitalTarget) * 100 : 0;
  const ownershipPercentage = financials.unitValue > 0 ? (investmentAmount / financials.unitValue) * 100 : 0;
  
  const getCurrentTier = (amount) => {
    for (let i = investorTiers.length - 1; i >= 0; i--) {
      if (amount >= investorTiers[i].threshold) return investorTiers[i];
    }
    return investorTiers[0];
  };

  const currentTier = getCurrentTier(investmentAmount);
  const cashbackAmount = (investmentAmount * currentTier.cashback) / 100;

  const formatINR = (val) => {
    if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val/100000).toFixed(2)} L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'unit-details', label: 'Unit Details' },
    { id: 'developer', label: 'Developer' },
    { id: 'rera', label: 'RERA' },
    { id: 'timeline', label: 'Timeline' },
    { id: 'location', label: 'Location' }
  ];

  return (
    <div className="p-10">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white mb-6 flex items-center gap-2">
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="mb-8">
        <h1 className="text-3xl font-light text-white mb-2">{property.name}</h1>
        <div className="flex items-center gap-2 text-gray-400">
          <MapPin className="w-4 h-4" />
          <p className="text-sm">{property.location}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="border border-white/10 mb-6">
            <img src={property.image} alt={property.name} className="w-full h-96 object-cover" />
          </div>

          <div className="border-b border-white/10 mb-6">
            <div className="flex gap-6">
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-3 text-sm border-b-2 ${activeTab === tab.id ? 'border-[#4ADE80] text-white' : 'border-transparent text-gray-500'}`}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            {activeTab === 'overview' && (
              <div data-testid="tab-overview">
                <h3 className="text-lg text-white mb-4">Project Details</h3>
                
                {/* Description */}
                {rawProject?.description && (
                  <p className="text-sm text-gray-400 mb-6 leading-relaxed">{rawProject.description}</p>
                )}
                
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="border border-white/10 p-4">
                    <p className="text-xs text-gray-600 mb-2">Property Type</p>
                    <p className="text-white text-sm capitalize">{(rawProject?.type || propData?.type || 'residence').replace(/_/g, ' ')}</p>
                  </div>
                  <div className="border border-white/10 p-4">
                    <p className="text-xs text-gray-600 mb-2">Expected ROI</p>
                    <p className="text-[#4ADE80] font-mono">{property.expectedROI}</p>
                  </div>
                  <div className="border border-white/10 p-4">
                    <p className="text-xs text-gray-600 mb-2">Expected Tenure</p>
                    <p className="text-white font-mono">{property.expectedTenure}</p>
                  </div>
                  <div className="border border-white/10 p-4">
                    <p className="text-xs text-gray-600 mb-2">Completion</p>
                    <p className="text-white">{property.completionDate}</p>
                  </div>
                </div>

                {/* Target Appreciation */}
                {rawProject?.targetAppreciation && (
                  <div className="bg-[#4ADE80]/5 border border-[#4ADE80]/20 p-4 mb-6">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Target Appreciation</p>
                    <p className="text-sm text-white">{rawProject.targetAppreciation}</p>
                  </div>
                )}

                {/* Geographical Area */}
                {rawProject?.geographicalArea && (
                  <div className="border border-white/10 p-4 mb-6">
                    <p className="text-xs text-gray-600 mb-2">Geographical Area</p>
                    <p className="text-white">{rawProject.geographicalArea}</p>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-4">Select a unit on the right to see specific details and invest</p>
              </div>
            )}

            {activeTab === 'unit-details' && (
              <div data-testid="tab-unit-details">
                <h3 className="text-lg text-white mb-4">Unit Specifications</h3>
                {selectedUnit ? (
                  <div className="space-y-6">
                    {/* Physical Details */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {selectedUnit.tower && (
                        <div className="border border-white/10 p-4">
                          <p className="text-xs text-gray-600 mb-2">Tower</p>
                          <p className="text-white">{selectedUnit.tower}</p>
                        </div>
                      )}
                      {selectedUnit.floor && (
                        <div className="border border-white/10 p-4">
                          <p className="text-xs text-gray-600 mb-2">Floor</p>
                          <p className="text-white">{selectedUnit.floor}</p>
                        </div>
                      )}
                      {selectedUnit.specifications?.configuration && (
                        <div className="border border-white/10 p-4">
                          <p className="text-xs text-gray-600 mb-2">Configuration</p>
                          <p className="text-white">{selectedUnit.specifications.configuration}</p>
                        </div>
                      )}
                      {selectedUnit.superArea && (
                        <div className="border border-white/10 p-4">
                          <p className="text-xs text-gray-600 mb-2">Super Area</p>
                          <p className="text-white font-mono">{selectedUnit.superArea} sq ft</p>
                        </div>
                      )}
                      {selectedUnit.carpetArea && (
                        <div className="border border-white/10 p-4">
                          <p className="text-xs text-gray-600 mb-2">Carpet Area</p>
                          <p className="text-white font-mono">{selectedUnit.carpetArea} sq ft</p>
                        </div>
                      )}
                      {selectedUnit.propertyType && (
                        <div className="border border-white/10 p-4">
                          <p className="text-xs text-gray-600 mb-2">Property Type</p>
                          <p className="text-white text-sm">{selectedUnit.propertyType}</p>
                        </div>
                      )}
                    </div>

                    {/* LLP Details */}
                    {selectedUnit.llp?.name && (
                      <div className="border border-white/10 p-5">
                        <h4 className="text-sm text-gray-400 uppercase tracking-wider mb-4">LLP Information</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-gray-600 mb-1">LLP Name</p>
                            <p className="text-white text-sm">{selectedUnit.llp.name}</p>
                          </div>
                          {selectedUnit.llpin && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">LLPIN</p>
                              <p className="text-white text-sm font-mono">{selectedUnit.llpin}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Agreement Terms */}
                    {selectedUnit.agreementTerms && (
                      <div className="border border-white/10 p-5">
                        <h4 className="text-sm text-gray-400 uppercase tracking-wider mb-4">Agreement Terms</h4>
                        <div className="grid grid-cols-2 gap-4">
                          {selectedUnit.agreementTerms.holdingPeriod && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Holding Period</p>
                              <p className="text-white text-sm">{selectedUnit.agreementTerms.holdingPeriod}</p>
                            </div>
                          )}
                          {selectedUnit.agreementTerms.targetAppreciation && (
                            <div>
                              <p className="text-xs text-gray-600 mb-1">Target Appreciation</p>
                              <p className="text-white text-sm">{selectedUnit.agreementTerms.targetAppreciation}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 border border-white/10">
                    <Building2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500">Select a unit to view its specifications</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'developer' && (
              <div data-testid="tab-developer">
                <div className="flex gap-5 mb-6 items-start">
                  {rawProject?.developer?.logoUrl ? (
                    <img src={rawProject.developer.logoUrl} alt={property.developer} className="w-16 h-16 object-contain border border-white/10 bg-white/5 p-1" />
                  ) : (
                    <div className="w-16 h-16 flex items-center justify-center border border-white/10 bg-white/5">
                      <Building2 className="w-8 h-8 text-gray-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl text-white">{property.developer}</h3>
                    <p className="text-sm text-gray-500">Developer</p>
                  </div>
                </div>
                {rawProject?.developer?.bio && (
                  <div className="border border-white/10 p-5 mb-6">
                    <p className="text-sm text-gray-400 leading-relaxed">{rawProject.developer.bio}</p>
                  </div>
                )}
                {rawProject?.developer?.rmName && (
                  <div className="border border-white/10 p-5">
                    <h4 className="text-sm text-gray-400 uppercase tracking-wider mb-4">Relationship Manager</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Name</p>
                        <p className="text-white text-sm">{rawProject.developer.rmName}</p>
                      </div>
                      {rawProject.developer.rmPhone && (
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Phone</p>
                          <p className="text-white text-sm font-mono">{rawProject.developer.rmPhone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'rera' && (
              <div data-testid="tab-rera">
                <div className="bg-green-500/10 border border-green-500/30 p-6 mb-6">
                  <p className="text-white">RERA Approved</p>
                </div>
                <p className="font-mono text-white mb-6">{property.reraNumber}</p>
                {rawProject?.rera?.certificateUrl && (
                  <a
                    href={rawProject.rera.certificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold text-sm uppercase tracking-wider transition-colors"
                    data-testid="view-rera-certificate-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View RERA Certificate
                  </a>
                )}
              </div>
            )}
            {activeTab === 'timeline' && (
              <div data-testid="tab-timeline">
                <h3 className="text-lg text-white mb-4">Project Timeline & Payment Schedule</h3>
                {selectedUnit && selectedUnit.paymentPlan && selectedUnit.paymentPlan.length > 0 ? (
                  <div className="space-y-0">
                    {selectedUnit.paymentPlan.map((slot, i) => {
                      const isFirst = i === 0;
                      const isLast = i === selectedUnit.paymentPlan.length - 1;
                      return (
                        <div key={i} className="flex gap-5" data-testid={`timeline-slot-${i}`}>
                          {/* Timeline line */}
                          <div className="flex flex-col items-center w-8 flex-shrink-0">
                            <div className={`w-3 h-3 border-2 ${isFirst ? 'bg-[#4ADE80] border-[#4ADE80]' : 'bg-transparent border-white/30'}`} />
                            {!isLast && <div className="w-px flex-1 bg-white/10" />}
                          </div>
                          {/* Content */}
                          <div className="flex-1 pb-6">
                            <div className="border border-white/10 p-4 hover:bg-white/[0.02] transition-colors">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="text-white font-medium">{slot.milestone}</p>
                                  {slot.description && <p className="text-xs text-gray-500 mt-1">{slot.description}</p>}
                                </div>
                                <span className="font-mono text-sm text-[#4ADE80] ml-4 flex-shrink-0">{slot.percentage}%</span>
                              </div>
                              {slot.expectedDate && (
                                <p className="text-xs text-gray-400 mt-2">
                                  Expected: {new Date(slot.expectedDate + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500 mb-4">Construction milestones for this project</p>
                    {property.milestones.map((m, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 border border-white/10 mb-2">
                        <div className={`w-3 h-3 flex-shrink-0 ${m.status === 'completed' ? 'bg-[#4ADE80]' : m.status === 'in-progress' ? 'bg-yellow-400' : 'bg-white/20'}`} />
                        <div className="flex-1">
                          <p className="text-white">{m.name}</p>
                          <p className="text-sm text-gray-500">{m.date}</p>
                        </div>
                        {m.percent > 0 && <p className="font-mono text-sm text-gray-400">{m.percent}%</p>}
                      </div>
                    ))}
                    <p className="text-xs text-gray-500 mt-3">Select a unit to view its payment milestone schedule</p>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'location' && (
              <div data-testid="tab-location">
                {rawProject?.location?.googleMapsEmbed ? (
                  <iframe
                    src={rawProject.location.googleMapsEmbed}
                    width="100%"
                    height="400"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Property Location"
                    className="border border-white/10"
                  />
                ) : (
                  <div className="bg-white/5 h-80 flex items-center justify-center border border-white/10">
                    <p className="text-gray-500">Map view — {property.location}{rawProject?.location?.state ? `, ${rawProject.location.state}` : ''}</p>
                  </div>
                )}
                <div className="mt-4 p-4 border border-white/10">
                  <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Address</p>
                  <p className="text-white">{rawProject?.location?.fullAddress || property.location}{rawProject?.location?.state ? `, ${rawProject.location.state}` : ''}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="border border-white/10 sticky top-6">
            <div className="bg-white/5 px-6 py-4 border-b border-white/10">
              <h2 className="text-sm font-medium text-white">Book Investment</h2>
            </div>

            <div className="p-6">
              {/* Unit Selection */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <p className="text-xs text-gray-600 uppercase mb-3">Step 1: Select Unit</p>
                {loadingUnits ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin w-5 h-5 border-2 border-[#4ADE80] border-t-transparent rounded-full" />
                  </div>
                ) : units.length === 0 ? (
                  <div className="text-center py-4 border border-white/10 bg-white/5">
                    <p className="text-sm text-gray-500">No units available</p>
                  </div>
                ) : (
                  <div className="relative">
                    <select
                      value={selectedUnit?.unitId || ''}
                      onChange={(e) => {
                        const unit = units.find(u => u.unitId === e.target.value);
                        setSelectedUnit(unit);
                        setInvestmentAmount(unit?.financial?.minInvestment || 500000);
                      }}
                      className="w-full bg-black border border-white/20 text-white px-4 py-3 pr-10 appearance-none cursor-pointer hover:border-[#4ADE80]/50 focus:border-[#4ADE80] focus:outline-none"
                      data-testid="unit-selector"
                    >
                      {units.map(unit => (
                        <option key={unit.unitId} value={unit.unitId}>
                          Unit {unit.unitName} • {unit.specifications?.size || 'N/A'} • {unit.specifications?.configuration || ''}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                  </div>
                )}
                
                {selectedUnit && (
                  <div className="mt-3 p-3 bg-white/5 border border-white/10">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-500">LLP</span>
                      <span className="text-white">{selectedUnit.llp?.name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-xs mb-2">
                      <span className="text-gray-500">Status</span>
                      <span className={`px-2 py-0.5 text-xs border ${UNIT_STATE_CONFIG[selectedUnit.lifecycle?.currentState]?.color || 'text-gray-400'}`}>
                        {UNIT_STATE_CONFIG[selectedUnit.lifecycle?.currentState]?.label || selectedUnit.status || 'Active'}
                      </span>
                    </div>
                    {selectedUnit.lifecycle?.currentState && selectedUnit.lifecycle.currentState !== 'OPEN' && (
                      <div className={`mt-2 p-2 text-xs ${
                        selectedUnit.lifecycle.currentState === 'FUNDED' ? 'bg-purple-500/10 text-purple-400' :
                        selectedUnit.lifecycle.currentState === 'PAUSED' ? 'bg-yellow-500/10 text-yellow-400' :
                        'bg-gray-500/10 text-gray-400'
                      }`}>
                        {selectedUnit.lifecycle.currentState === 'FUNDED' && '🎉 This unit is fully funded!'}
                        {selectedUnit.lifecycle.currentState === 'PAUSED' && '⏸️ Investment temporarily paused'}
                        {selectedUnit.lifecycle.currentState === 'DRAFT' && '🔒 Coming soon - not yet open'}
                        {selectedUnit.lifecycle.currentState === 'CLOSED' && '✅ Unit closed - funds released'}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Funding Status */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <p className="text-xs text-gray-600 uppercase mb-4">Unit Financials</p>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div><p className="text-xs text-gray-600 mb-1">Unit Value</p><p className="font-mono text-sm text-white">{formatINR(financials.unitValue)}</p></div>
                  <div><p className="text-xs text-gray-600 mb-1">Capital Target</p><p className="font-mono text-sm text-white">{formatINR(financials.capitalTarget)}</p></div>
                  <div><p className="text-xs text-gray-600 mb-1">Capital Funded</p><p className="font-mono text-sm text-[#4ADE80]">{formatINR(financials.capitalRaised)}</p></div>
                  <div><p className="text-xs text-gray-600 mb-1">Balance</p><p className="font-mono text-sm text-white">{formatINR(balanceToFund)}</p></div>
                </div>
                <div><div className="flex justify-between text-xs mb-2"><span className="text-gray-600">Progress</span><span className="font-mono text-white">{fundingPercentage.toFixed(1)}%</span></div><div className="h-1 bg-white/10"><div className="h-full bg-[#4ADE80]" style={{ width: `${Math.min(fundingPercentage, 100)}%` }} /></div></div>
              </div>

              {/* Investment Amount */}
              <div className="mb-6">
                <p className="text-xs text-gray-600 uppercase mb-3">Step 2: Investment Amount</p>
                <div className="flex justify-between mb-3"><span className="text-sm text-gray-500">Amount</span><span className="font-mono text-xl text-white">{formatINR(investmentAmount)}</span></div>
                <input 
                  type="range" 
                  min={financials.minInvestment} 
                  max={financials.maxInvestment} 
                  step={100000} 
                  value={investmentAmount} 
                  onChange={(e) => setInvestmentAmount(parseInt(e.target.value))} 
                  className="w-full h-px bg-white/10 slider-thumb"
                  disabled={!selectedUnit}
                  data-testid="investment-slider"
                />
                <div className="flex justify-between text-xs text-gray-600 mt-2"><span>{formatINR(financials.minInvestment)}</span><span>{formatINR(financials.maxInvestment)}</span></div>
              </div>

              {/* Ownership Share */}
              <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/30 p-4 mb-4">
                <p className="text-xs text-gray-400 mb-1">Your Share</p>
                <p className="font-mono text-3xl text-[#4ADE80]">{ownershipPercentage.toFixed(2)}%</p>
                <p className="text-xs text-gray-500 mt-1">of Unit {selectedUnit?.unitName || ''}</p>
              </div>

              {/* Cashback */}
              <div className="bg-purple-500/10 border border-purple-500/30 p-4 mb-6">
                <div className="flex justify-between mb-2"><p className="text-xs text-gray-400">Cashback ({currentTier.name})</p><span className="text-xs">{currentTier.icon}</span></div>
                <p className="font-mono text-2xl text-white">{formatINR(cashbackAmount)}</p>
              </div>

              {/* Call Advisor Button */}
              {property.advisor?.phone && (
                <a
                  href={`tel:${property.advisor.phone.replace(/\s/g, '')}`}
                  className="w-full mb-3 border border-white/20 hover:border-white/40 bg-white/5 hover:bg-white/10 text-white font-medium py-3 flex items-center justify-center gap-2 transition-all"
                  data-testid="call-advisor-btn"
                >
                  <Phone className="w-4 h-4" />
                  Call Advisor
                </a>
              )}
              
              {/* WhatsApp Advisor Button (optional) */}
              {property.advisor?.whatsApp && (
                <a
                  href={`https://wa.me/${property.advisor.whatsApp.replace(/[^0-9]/g, '')}?text=Hi, I'm interested in ${property.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full mb-4 border border-green-500/30 hover:border-green-500/50 bg-green-500/10 hover:bg-green-500/20 text-green-400 font-medium py-3 flex items-center justify-center gap-2 transition-all"
                  data-testid="whatsapp-advisor-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              )}

              {/* Book Button */}
              {(() => {
                const unitState = selectedUnit?.lifecycle?.currentState || 'DRAFT';
                const stateConfig = UNIT_STATE_CONFIG[unitState] || UNIT_STATE_CONFIG.DRAFT;
                const canInvest = selectedUnit && stateConfig.canInvest;
                
                return (
                  <>
                    <button 
                      onClick={() => setShowBookingModal(true)}
                      disabled={!canInvest}
                      className={`w-full font-semibold py-4 flex items-center justify-center gap-2 transition-all ${
                        canInvest 
                          ? 'bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black cursor-pointer' 
                          : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      }`}
                      data-testid="book-investment-btn"
                    >
                      {!selectedUnit ? (
                        <>Select a Unit First</>
                      ) : !canInvest ? (
                        <>
                          <Lock className="w-4 h-4" />
                          {unitState === 'FUNDED' ? 'Fully Funded' : 
                           unitState === 'PAUSED' ? 'Investment Paused' :
                           unitState === 'DRAFT' ? 'Coming Soon' :
                           'Not Available'}
                        </>
                      ) : (
                        <>
                          Book Investment
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                    
                    {canInvest && (
                      <p className="text-xs text-gray-600 text-center mt-4">₹25K EOI Fee • Refundable</p>
                    )}
                    
                    {unitState === 'FUNDED' && (
                      <p className="text-xs text-purple-400 text-center mt-4">
                        Join waitlist for similar opportunities
                      </p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Investment Booking Modal */}
      <InvestmentBookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        unit={selectedUnit}
        project={property}
        investmentAmount={investmentAmount}
        onSuccess={(investmentId) => {
          // Navigate to portfolio or show success
          if (onBookInvestment) {
            onBookInvestment(property.id, investmentAmount, selectedUnit?.unitId, investmentId);
          }
        }}
      />
    </div>
  );
}
